import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { idempotencyHeader } from '../services/idempotency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const IS_DEV = process.env.NODE_ENV === 'development';
const devLog = (...args) => { if (IS_DEV) console.log(...args); };
const devError = (...args) => { if (IS_DEV) console.error(...args); };

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('superkawa_token'));
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(localStorage.getItem('superkawa_role') || 'customer');
  // A3.2-prep-2: capture staff multi-tenant binding from TokenResponse (read-only side-channel)
  const [staffShopId, setStaffShopId] = useState(() => {
    const v = localStorage.getItem('superkawa_staff_shop_id');
    return v === 'null' || v === '' || v === null ? null : v;
  });
  const [staffShopIds, setStaffShopIds] = useState(() => {
    try {
      const v = localStorage.getItem('superkawa_staff_shop_ids');
      return v ? JSON.parse(v) : null;
    } catch {
      return null;
    }
  });

  const fetchUser = useCallback(async (countryCode, shopId, signal) => {
    devLog('[auth] FETCH_USER_START', {
      hasToken: Boolean(token),
      role: localStorage.getItem('superkawa_role'),
      countryCodeParam: countryCode,
      shopIdParam: shopId,
    });
    if (!token) {
      devLog('[auth] FETCH_USER_SKIP_NO_TOKEN');
      setLoading(false);
      return null;
    }
    // MB-WLT-4 — Resolve country_code + shop_id : param explicite > boutique persistée > fallback.
    // Le shop_id permet au backend de renvoyer un solde wallet + points STRICTEMENT par boutique
    // (get_effective_wallet_balance). Sans boutique sélectionnée → fallback pays legacy.
    let cc = countryCode;
    let sid = shopId;
    if (!cc || !sid) {
      try {
        // P0-F-1 — Lire la clé customer dédiée, fallback legacy 'superkawa_shop' (1 release)
        const raw = localStorage.getItem('superkawa_customer_shop') || localStorage.getItem('superkawa_shop');
        const saved = raw ? JSON.parse(raw) : null;
        if (!cc) cc = saved?.country_code;
        if (!sid) sid = saved?.id;
      } catch {}
    }
    try {
      const qs = new URLSearchParams();
      if (cc) qs.set('country_code', cc);
      if (sid) qs.set('shop_id', sid);
      const params = qs.toString() ? `?${qs.toString()}` : '';
      const response = await axios.get(`${API}/users/me${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      devLog('[auth] FETCH_USER_RESULT', {
        httpStatus: response.status,
        hasData: Boolean(response.data),
        hasId: Boolean(response.data?.id),
        hasQrCode: Boolean(response.data?.qr_code),
        userId: response.data?.id,
        qrCode: response.data?.qr_code,
        loyaltyPoints: response.data?.loyalty_points,
        walletBalance: response.data?.wallet_balance,
        responseKeys: response.data ? Object.keys(response.data) : null,
      });
      // MB-APP-WALLET-QR-PERSIST-FIX — Fusion (pas de remplacement brutal) : préserve
      // qr_code si une réponse /users/me incomplète ne le fournit pas (sinon le QR du
      // Portefeuille s'efface définitivement et reste en spinner).
      setUser(prev => ({ ...prev, ...response.data, qr_code: response.data?.qr_code || prev?.qr_code }));
      return response.data;
    } catch (error) {
      // MB-WALLET-P1B — Annulation volontaire (AbortController lors d'un changement rapide
      // de boutique) : silencieux, aucun logout, aucune erreur affichée.
      if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
        return null;
      }
      devError('[auth] FETCH_USER_FAILED', {
        message: error?.message,
        name: error?.name,
        httpStatus: error?.response?.status,
        responseData: error?.response?.data,
        url: error?.config?.url,
        isNetworkError: !error?.response,
      });
      // Only logout on 401/403, not on other errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        devLog('[auth] LOGOUT_TRIGGERED_BY_401_403');
        logout();
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = (newToken, userData, userRole = 'customer', staffShopInfo = null) => {
    localStorage.setItem('superkawa_token', newToken);
    localStorage.setItem('superkawa_role', userRole);
    setToken(newToken);
    setUser(userData);
    setRole(userRole);
    // A3.2-prep-2: persist staff shop binding (optional, only for staff logins)
    const sid = staffShopInfo?.shop_id ?? null;
    const sids = staffShopInfo?.shop_ids ?? null;
    if (sid) localStorage.setItem('superkawa_staff_shop_id', sid);
    else localStorage.removeItem('superkawa_staff_shop_id');
    if (sids && Array.isArray(sids) && sids.length > 0) {
      localStorage.setItem('superkawa_staff_shop_ids', JSON.stringify(sids));
    } else {
      localStorage.removeItem('superkawa_staff_shop_ids');
    }
    setStaffShopId(sid);
    setStaffShopIds(sids && Array.isArray(sids) && sids.length > 0 ? sids : null);
  };

  const logout = () => {
    localStorage.removeItem('superkawa_token');
    localStorage.removeItem('superkawa_role');
    localStorage.removeItem('superkawa_staff_shop_id');
    localStorage.removeItem('superkawa_staff_shop_ids');
    setToken(null);
    setUser(null);
    setRole('customer');
    setStaffShopId(null);
    setStaffShopIds(null);
  };

  const refreshUser = useCallback(async (countryCode, shopId, signal) => {
    return await fetchUser(countryCode, shopId, signal);
  }, [fetchUser]);

  // MB-NAV-1 : flag plateforme dérivé du profil /users/me (allowlist env côté backend)
  const isPlatformAdmin = Boolean(user?.is_platform_admin);

  return (
    <AuthContext.Provider value={{ user, token, loading, role, login, logout, refreshUser, staffShopId, staffShopIds, isPlatformAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Context
const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

export const CartProvider = ({ children }) => {
  // Load cart from localStorage on init
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('superkawa_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [selectedStore, setSelectedStore] = useState(null);

  // Persist cart to localStorage
  useEffect(() => {
    localStorage.setItem('superkawa_cart', JSON.stringify(items));
  }, [items]);

  const addItem = (product, variant = null, quantity = 1) => {
    const itemKey = `${product.id}-${variant || 'default'}`;
    const price = product.base_price + (variant ? (product.variants?.find(v => v.name === variant)?.price_modifier || 0) : 0);
    
    setItems(prev => {
      const existing = prev.find(item => item.key === itemKey);
      if (existing) {
        return prev.map(item => 
          item.key === itemKey 
            ? { ...item, quantity: item.quantity + quantity, total_price: (item.quantity + quantity) * item.unit_price }
            : item
        );
      }
      return [...prev, {
        key: itemKey,
        product_id: product.id,
        product_name: product.name,
        variant: variant,
        quantity: quantity,
        unit_price: price,
        total_price: price * quantity,
        image_url: product.image_url
      }];
    });
  };

  const removeItem = (key) => {
    setItems(prev => prev.filter(item => item.key !== key));
  };

  const updateQuantity = (key, quantity) => {
    if (quantity <= 0) {
      removeItem(key);
      return;
    }
    setItems(prev => prev.map(item => 
      item.key === key 
        ? { ...item, quantity, total_price: quantity * item.unit_price }
        : item
    ));
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem('superkawa_cart');
  };

  const total = items.reduce((sum, item) => sum + item.total_price, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ 
      items, 
      addItem, 
      removeItem, 
      updateQuantity, 
      clearCart, 
      total, 
      itemCount,
      selectedStore,
      setSelectedStore 
    }}>
      {children}
    </CartContext.Provider>
  );
};

// API Helper
export const api = {
  get: (url, token, config = {}) => axios.get(`${API}${url}`, { ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}), ...config }),
  // MB-IDEMPOTENCY-FIX-1f — injection transparente de Idempotency-Key sur les POST critiques.
  post: (url, data, token) => axios.post(`${API}${url}`, data, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...idempotencyHeader(url, data),
    },
  }),
  put: (url, data, token) => axios.put(`${API}${url}`, data, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  patch: (url, data, token) => axios.patch(`${API}${url}`, data, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  delete: (url, token) => axios.delete(`${API}${url}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  upload: (url, formData, token) => axios.post(`${API}${url}`, formData, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'multipart/form-data' } }),
};

export { API, BACKEND_URL };
